import {
  faBold,
  faItalic,
  faListUl,
  faListOl,
  faLink,
  faHeading,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Editor as TiptapEditor } from '@tiptap/react';
import { EditorContent, Extension, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { useFormikContext } from 'formik';
import type { Options as RehypeParseOptions } from 'node_modules/rehype-parse/lib';
import type { Options as RemarkStringifyOptions } from 'node_modules/remark-stringify/lib';
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useState,
  useRef,
  type CSSProperties,
} from 'react';
import rehypeParse from 'rehype-parse';
import rehypeRaw from 'rehype-raw';
import rehypeRemark from 'rehype-remark';
import rehypeSanitize from 'rehype-sanitize';
import rehypeStringify from 'rehype-stringify';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import remarkStringify from 'remark-stringify';
import { toast } from 'sonner';
import { unified } from 'unified';
import { Button } from 'src/components/buttons-and-actions/button';
import { VerticalDivider } from 'src/components/dividers';
import { HeadingPopover } from 'src/components/form/editor-heading-popover';
import { LinkPopover } from 'src/components/form/editor-link-popover';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import { FieldLabel } from 'src/components/form/label';

import { useCustomField } from 'src/components/form/use-custom-field';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';

export type Props = {
  mode?: 'default' | 'chat';
  stacked?: boolean;
  nested?: boolean;
  slim?: boolean;
  autofocus?: boolean;
} & FormFieldProps;

/**
 * Use this component whenever a user needs to provide Markdown input.
 *
 * For shorter text input you can use [Input](../?path=/docs/components-form-input--docs).
 *
 * If the user needs to provide longer text without Markdown formatting use [Textarea](../?path=/docs/components-form-textarea--docs) instead.
 */
export const Editor = (props: Props) => {
  const { isStacked, minTablet } = useDefaultStacked();
  const {
    label,
    name,
    mode = 'default',
    stacked = isStacked,
    nested,
    slim,
    hideLabel = false,
    disabled,
    disabledMessage,
    hint,
    info,
    markAsRequired,
    autofocus = false,
  } = props;
  const id = useId();
  const [field, showError, error, helper] = useCustomField<string | null>(
    name,
    label
  );
  const formGroup = useFieldGroup();
  const isNewDesignActive = useIsNewDesignActive();
  const { submitForm } = useFormikContext();

  const [toolbarItemStates, setToolbarItemStates] = useState({
    bold: { isActive: false },
    italic: { isActive: false },
    orderedList: { isActive: false },
    bulletList: { isActive: false },
    heading: {
      isActive: false,
      level: null as number | null,
    },
    link: { isActive: false },
  });

  const [linkPopoverActive, setLinkPopoverActive] = useState(false);
  const linkButtonRef = useRef<HTMLButtonElement>(null);

  const [headingPopoverActive, setHeadingPopoverActive] = useState(false);
  const headingButtonRef = useRef<HTMLButtonElement>(null);
  const isInternalUpdateRef = useRef(false);
  const isMountedRef = useRef(false);

  const errorColor = isNewDesignActive
    ? 'var(--color-foreground-support-error)'
    : 'var(--color-legacy-error-dark)';
  const errorBgColor = isNewDesignActive
    ? 'var(--color-background-support-error-minimal)'
    : 'var(--color-legacy-error-lighter)';
  const disabledColor = isNewDesignActive
    ? 'var(--color-foreground-surface-disabled)'
    : 'var(--color-legacy-inactive)';
  const disabledBgColor = isNewDesignActive
    ? 'var(--color-background-surface-disabled)'
    : 'var(--color-legacy-inactive-lighter)';
  const regularColor = isNewDesignActive
    ? 'var(--color-foreground-surface-default)'
    : 'var(--color-legacy-brand)';
  const regularBgColor = isNewDesignActive ? 'white' : 'white';

  const primaryColor = disabled
    ? disabledColor
    : showError
      ? errorColor
      : regularColor;
  const secondaryColor = disabled
    ? disabledBgColor
    : showError
      ? errorBgColor
      : regularBgColor;

  const fieldId = formGroup?.id || id;
  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;
  const ariaLabel = hideLabel ? displayLabel : undefined;

  const LinkShortcut = Extension.create({
    name: 'linkShortcut',
    addKeyboardShortcuts() {
      return {
        'Mod-k': () => {
          if (isMountedRef.current) setLinkPopoverActive(true);
          return true;
        },
      };
    },
  });
  const NewLineShortcut = Extension.create({
    name: 'NewLineShortcut',
    addKeyboardShortcuts() {
      return {
        'Shift-Enter': ({ editor }) =>
          editor.commands.first(({ commands }) => [
            () => commands.newlineInCode(),
            () => commands.splitListItem('listItem'),
            () => commands.createParagraphNear(),
            () => commands.liftEmptyBlock(),
            () => commands.splitBlock(),
          ]),
      };
    },
  });
  const SubmitShortcut = Extension.create({
    name: 'SubmitShortcut',
    addKeyboardShortcuts() {
      return {
        Enter: ({ editor }) => {
          if (editor.getText().trim()) {
            submitForm();
          }
          return true;
        },
      };
    },
  });

  const updateToolbarItemStates = useCallback((editor: TiptapEditor) => {
    if (!isMountedRef.current) return;
    setToolbarItemStates({
      bold: { isActive: editor.isActive('bold') },
      italic: { isActive: editor.isActive('italic') },
      orderedList: { isActive: editor.isActive('orderedList') },
      bulletList: { isActive: editor.isActive('bulletList') },
      heading: {
        isActive: editor.isActive('heading'),
        level: editor.isActive('heading')
          ? editor.getAttributes('heading').level
          : null,
      },
      link: { isActive: editor.isActive('link') },
    });
  }, []);

  const htmlToMarkdown = useCallback(async (html: string) => {
    try {
      const result = await unified()
        .use(rehypeParse, { fragment: true } satisfies RehypeParseOptions)
        .use(rehypeRemark)
        .use(remarkGfm)
        .use(remarkStringify, {
          resourceLink: true,
          emphasis: '_',
        } satisfies RemarkStringifyOptions)
        .process(html);
      return String(result).trimEnd();
    } catch (error) {
      toast.error('An unexpected error occurred in the editor.');
    }
  }, []);
  const markdownToHtml = useCallback(async (markdown: string) => {
    try {
      const result = await unified()
        .use(remarkParse)
        .use(remarkGfm)
        .use(remarkRehype)
        .use(rehypeRaw)
        .use(rehypeSanitize)
        .use(rehypeStringify)
        .process(markdown);
      return String(result);
    } catch (error) {
      toast.error('An unexpected error occurred in the editor.');
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          defaultProtocol: 'https',
          openOnClick: false,
        },
        // Disable extensions that are not integrated yet (since they could cause errors without proper implementation)
        blockquote: false,
        code: false,
        codeBlock: false,
        dropcursor: false,
        gapcursor: false,
        hardBreak: false,
        horizontalRule: false,
        strike: false,
        trailingNode: false,
        underline: false,
      }),
      LinkShortcut,
      ...(mode === 'chat' ? [NewLineShortcut, SubmitShortcut] : []),
    ],
    content: field.value ?? '',
    editable: !disabled,
    autofocus,
    onUpdate: ({ editor }) => {
      if (disabled) return;

      updateToolbarItemStates(editor);

      // Mark this as an internal update to prevent cursor position issues
      isInternalUpdateRef.current = true;

      const htmlContent = editor.getHTML();
      htmlToMarkdown(htmlContent).then((markdown) => {
        helper.setValue(markdown || null);
        // Reset the internal update flag after a short delay to allow the effect to run
        setTimeout(() => {
          isInternalUpdateRef.current = false;
        }, 10);
      });
    },
    onSelectionUpdate: ({ editor }) => {
      if (disabled) return;

      updateToolbarItemStates(editor);

      const isLinkActive = editor.isActive('link');
      if (isMountedRef.current && isLinkActive && !linkPopoverActive) {
        setLinkPopoverActive(true);
      } else if (isMountedRef.current && !isLinkActive && linkPopoverActive) {
        setLinkPopoverActive(false);
      }
    },
    onTransaction: ({ editor }) => {
      if (disabled) return;

      updateToolbarItemStates(editor);
    },
    editorProps: {
      attributes: {
        class: cn(`
          min-h-0 flex-1 overflow-y-auto border-none wrap-anywhere
          whitespace-pre-wrap shadow-none outline-none
        `),
        'data-testid': field.name,
      },
    },
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  // Keep editor in sync with Formik value (for reset)
  useEffect(() => {
    // Skip updates if this change originated from the editor itself
    if (isInternalUpdateRef.current) return;

    const currentMarkdown = field.value ?? '';

    if (currentMarkdown) {
      markdownToHtml(currentMarkdown).then((html) => {
        // Only update if the content is different to avoid infinite loops
        if (!html || html === editor.getHTML()) return;
        // Store current cursor position
        const { from, to } = editor.state.selection;

        editor.commands.setContent(html, { emitUpdate: false });

        // Restore cursor position if it's within the new content bounds
        const docSize = editor.state.doc.content.size;
        const validFrom = Math.min(from, docSize);
        const validTo = Math.min(to, docSize);

        // Only restore position if it's a valid position in the document
        if (validFrom <= docSize && validTo <= docSize) {
          editor.commands.setTextSelection({ from: validFrom, to: validTo });
        }
      });
    } else {
      // If no value, clear the editor
      if (editor.getHTML() !== '<p></p>') {
        editor.commands.setContent('', { emitUpdate: false });
      }
    }
  }, [field.value, editor, markdownToHtml]);

  type ToolbarItem = {
    icon: IconDefinition;
    label: string;
    action: () => void;
    isActive: () => boolean;
  };
  const toolbar: ToolbarItem[][] = [
    [
      {
        icon: faBold,
        label: 'Bold',
        action: () => editor.chain().focus().toggleBold().run(),
        isActive: () => toolbarItemStates.bold.isActive,
      },
      {
        icon: faItalic,
        label: 'Italic',
        action: () => editor.chain().focus().toggleItalic().run(),
        isActive: () => toolbarItemStates.italic.isActive,
      },
    ],
    [
      {
        icon: faListUl,
        label: 'Bullet List',
        action: () => editor.chain().focus().toggleBulletList().run(),
        isActive: () => toolbarItemStates.bulletList.isActive,
      },
      {
        icon: faListOl,
        label: 'Numbered List',
        action: () => editor.chain().focus().toggleOrderedList().run(),
        isActive: () => toolbarItemStates.orderedList.isActive,
      },
    ],
    [
      {
        icon: faHeading,
        label: 'Heading',
        action: () => {
          if (isMountedRef.current)
            setHeadingPopoverActive(!headingPopoverActive);
        },
        isActive: () => toolbarItemStates.heading.isActive,
      },
    ],
    [
      {
        icon: faLink,
        label: 'Link',
        action: () => {
          if (isMountedRef.current) setLinkPopoverActive(!linkPopoverActive);
        },
        isActive: () => toolbarItemStates.link.isActive,
      },
    ],
  ];

  const fieldItem = (
    <FieldItem>
      <Tooltip
        content={disabled && disabledMessage ? disabledMessage : undefined}
      >
        {(targetProps) => (
          <div
            className={cn(
              'rounded-xs border',
              'flex h-full flex-1 flex-col',
              `
                not-legacy:has-[.tiptap:focus]:focus-style
                legacy:has-[.tiptap:focus]:legacy-focus-style
              `,
              'legacy:rounded-[0.188rem]',
              disabled && 'cursor-not-allowed opacity-60',
              showError && 'border-border-support-error-default',
              slim ? 'min-h-4xl' : 'min-h-[150px]'
            )}
            style={
              {
                '--color-bd-default': primaryColor,
                '--color-bg-default': secondaryColor,
                '--color-fg-default': primaryColor,
                borderColor: 'var(--color-bd-default)',
                background: 'var(--color-bg-default)',
                color: 'var(--color-fg-default)',
                pointerEvents: disabled ? 'none' : undefined,
              } as CSSProperties
            }
          >
            <div
              className={cn(
                'flex h-4xl items-center gap-xs border-b px-md py-xs'
              )}
              style={{
                borderColor: 'var(--color-bd-default)',
                color: 'var(--color-fg-default)',
              }}
            >
              {toolbar.map((row, rowIndex) => (
                <Fragment key={rowIndex}>
                  {row.map((item) => (
                    <Fragment key={item.label}>
                      <Tooltip
                        content={!disabled ? <>{item.label}</> : undefined}
                        noTabIndex // Handled with the toolbar buttons
                      >
                        {(toolbarTargetProps) => (
                          <div>
                            <Button
                              {...toolbarTargetProps}
                              ref={
                                item.label === 'Link'
                                  ? linkButtonRef
                                  : item.label === 'Heading'
                                    ? headingButtonRef
                                    : undefined
                              }
                              mode="icon"
                              type="button"
                              aria-label={item.label}
                              aria-pressed={item.isActive()}
                              onClick={item.action}
                              disabled={disabled || !editor}
                              className={cn(
                                `
                                  flex min-h-3xl w-full min-w-3xl cursor-pointer
                                  items-center justify-center gap-md rounded-xs
                                  px-md py-sm text-start
                                  text-foreground-surface-default
                                  transition-[color,border-color,background-color]
                                  duration-150 ease-in-out
                                  hover:bg-background-surface-hover
                                  hover:text-foreground-surface-default!
                                  motion-reduce:transition-none
                                  legacy:rounded-[0.188rem]
                                `,
                                // Exceptionally we unset the given "spaced" focus style to Button mode="icon" because of spacing issues
                                `
                                  focus-visible:-outline-offset-2!
                                  focus-visible:aria-pressed:contrast-focus-style
                                `
                              )}
                              style={{ color: 'var(--color-fg-default)' }}
                            >
                              <FontAwesomeIcon icon={item.icon} />
                            </Button>
                          </div>
                        )}
                      </Tooltip>
                    </Fragment>
                  ))}
                  {rowIndex < toolbar.length - 1 && (
                    <VerticalDivider
                      className={cn(
                        showError &&
                          !disabled &&
                          `
                            bg-border-support-error-default
                            legacy:bg-legacy-error-light
                          `
                      )}
                    />
                  )}
                </Fragment>
              ))}
            </div>
            <div className="relative flex min-h-4xl flex-1">
              <EditorContent
                {...targetProps}
                editor={editor}
                id={fieldId}
                aria-label={ariaLabel}
                className={cn(
                  `
                    flex max-h-[23.5rem] w-full flex-1 px-md py-sm
                    text-body-base
                  `,
                  'bg-transparent',
                  'placeholder:text-foreground-surface-subdued',
                  disabled && 'pointer-events-none'
                )}
                style={{ color: 'var(--color-fg-default)' }}
              />
              {/* Hidden input to store the form value for testing */}
              <input
                type="hidden"
                {...field}
                value={field.value ?? ''}
                data-testid={`${field.name}-value`}
              />
              <LinkPopover
                editor={editor}
                isActive={linkPopoverActive}
                setIsActive={setLinkPopoverActive}
                triggerRef={linkButtonRef}
              />
              <HeadingPopover
                editor={editor}
                isActive={headingPopoverActive}
                setIsActive={setHeadingPopoverActive}
                triggerRef={headingButtonRef}
              />
              <style>{`
                /* Editor content styles */
                .tiptap h1 {
                  font-family: var(--font-family-headline);
                  font-weight: var(--font-headline);
                  font-size: var(--text-headline-lg);
                  line-height: 1.5;
                  color: var(--color-foreground-default);
                  margin: 0;
                  padding: 0;
                }
                .tiptap h2 {
                  font-family: var(--font-family-headline);
                  font-weight: var(--font-headline);
                  font-size: var(--text-headline-md);
                  line-height: 1.5;
                  color: var(--color-foreground-default);
                  margin: 0;
                  padding: 0;
                }
                .tiptap h3 {
                  font-family: var(--font-family-title);
                  font-weight: var(--font-title);
                  font-size: var(--text-title-md);
                  line-height: 1.6;
                  color: var(--color-foreground-default);
                  margin: 0;
                  padding: 0;
                }
                .tiptap h4,
                .tiptap h5,
                .tiptap h6 {
                  font-family: var(--font-family-body);
                  font-weight: 700;
                  font-size: var(--text-body-base);
                  line-height: 1.3;
                  color: var(--color-foreground-default);
                  margin: 0;
                  padding: 0;
                }
                .tiptap a {
                  color: var(--color-foreground-primary-inverted);
                  text-decoration: underline;
                  text-decoration-color: var(--color-legacy-brand-light-2);
                  cursor: pointer;
                  transition: color 0.15s ease-in-out;
                }
                .tiptap a:hover {
                  color: var(--color-foreground-secondary-hover);
                }
                .tiptap a:active {
                  color: var(--color-foreground-secondary-active);
                }
                .tiptap a:focus {
                  outline: none;
                  color: var(--color-foreground-secondary-hover);
                }

                /* Legacy styles */
                [data-theme=legacy] .tiptap h1 {
                  font-family: var(--font-legacy-heading);
                  font-weight: 900;
                  font-size: 4.5rem;
                  line-height: 6.75rem;
                  letter-spacing: -0.1rem;
                  color: var(--color-legacy-brand);
                }
                [data-theme=legacy] .tiptap h2 {
                  font-family: var(--font-legacy-heading);
                  font-weight: 900;
                  font-size: 3rem;
                  line-height: 4.5rem;
                  letter-spacing: -0.06rem;
                  color: var(--color-legacy-brand);
                }
                [data-theme=legacy] .tiptap h3 {
                  font-family: var(--font-legacy-heading);
                  font-weight: 900;
                  font-size: 2rem;
                  line-height: 3.2rem;
                  letter-spacing: -0.04rem;
                  color: var(--color-legacy-brand);
                }
                [data-theme=legacy] .tiptap h4,
                [data-theme=legacy] .tiptap h5,
                [data-theme=legacy] .tiptap h6 {
                  font-family: var(--font-legacy-base);
                  font-weight: 700;
                  font-size: 1.4rem;
                  line-height: 1.83rem;
                  color: var(--color-legacy-brand);
                }
                [data-theme=legacy] .tiptap a {
                  color: var(--color-legacy-brand);
                  text-decoration: none;
                  border-bottom: 2px solid transparent;
                  transition: color 0.35s ease-in-out;
                }
                [data-theme=legacy] .tiptap a:hover {
                  color: var(--color-legacy-brand-secondary);
                }
                [data-theme=legacy] .tiptap a:focus {
                  color: var(--color-legacy-brand-secondary);
                }
              `}</style>
            </div>
          </div>
        )}
      </Tooltip>

      {(hint || mode === 'chat') && (
        <FormHint isDisabled={disabled}>
          {hint ?? 'Shift + Enter: New Line | Enter: Send'}
        </FormHint>
      )}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
    </FieldItem>
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout stacked={stacked}>
      <FieldLabel
        name={name}
        displayLabel={displayLabel}
        fieldId={fieldId}
        info={info}
        showError={showError}
        nested={nested}
      />
      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
};
